import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate } from 'gatsby'
import ApolloClient, { gql } from 'apollo-boost'
import { useQuery } from '@apollo/react-hooks'
import fetch from 'isomorphic-fetch'
import tw, { css, styled } from 'twin.macro'

import { Menu, MenuList, MenuButton, MenuItem } from '@reach/menu-button'
import { CustomCheckboxContainer, CustomCheckboxInput } from '@reach/checkbox'
import {
	Slider,
	SliderInput,
	SliderTrack,
	SliderTrackHighlight,
	SliderHandle,
	SliderMarker,
} from '@reach/slider'

import Layout from '../components/layout'
import Meta from '../components/meta'
import Round from '../components/svg/round-pool'
import Rectangular from '../components/svg/rectangular-pool'
import Complex from '../components/svg/complex-pool'
import useProduct from '../hooks/useProduct'
import useWindowResize from '../hooks/useWindowResize'
// import { Client } from '../apollo/client'
const windowGlobal = typeof window !== 'undefined' && window

const client = new ApolloClient({
	uri: `https://graphql.contentful.com/content/v1/spaces/${process.env.GATSBY_CONTENTFUL_SPACE_ID}/environments/master?access_token=${process.env.GATSBY_CONTENTFUL_DELIVERY_TOKEN}`,
	fetch,
})

const Banner = styled.div`
	${tw`w-full h-32 md:h-48 lg:h-64 mb-12 bg-no-repeat bg-cover bg-center`}
	background-image: url(${({ bg }) => bg});
`
const Heading = styled.h1`
	${tw`font-display font-bold text-gray-800 my-4 text-center md:text-left`}
	font-size: 3rem;
`
const SubHead = styled.h2`
	${tw`font-sans text-gray-700 text-base mb-2 text-center md:text-left`}
`
const Label = styled.label`
	${tw`flex items-center uppercase tracking-wide text-xs font-normal text-gray-700 mb-3 font-bold`}
`
const SelectButton = styled(MenuButton)`
	${tw`flex appearance-none py-2 pl-4 pr-2 items-center`}
	${tw`leading-tight`}
	${tw`bg-white text-gray-500`}
	${tw`focus:shadow-outline focus:outline-none`}
	${tw`border-2 border-black rounded`}
	&:focus {
		box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.25);
	}
`
const SelectList = styled(MenuList)``
const SelectItem = styled(MenuItem)``
const SelectIcon = styled.span`
	${tw`pointer-events-none flex items-center px-2 text-gray-700`}
	>svg {
		${tw`fill-current h-4 w-4`}
	}
`

const ContentsHolder = tw.div`flex flex-col lg:flex-row`
const LeftContent = tw.div`flex flex-col w-full mx-auto sm:w-6/12 md:w-7/12 lg:w-3/12`
const RightContent = tw.div`flex flex-col w-full lg:w-9/12 lg:ml-2`
const Line = styled.div`
	${tw`my-3 border-t-2 border-gray-400 h-auto`}
`
const Inputs = styled.div`
	${tw`font-display flex flex-row mt-4`}

	input[type=number]::-webkit-inner-spin-button,
	input[type=number]::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	input {
		${tw`border-2 border-gray-700 rounded text-center p-2 mx-3`}
	}

	> p {
		${tw`justify-center items-center flex text-sm`}
	}
`
const PoolSize = styled.div`
	${tw`flex flex-col justify-between w-full px-1 md:px-2 pb-8`}

	[data-reach-slider-orientation="vertical"] {
		height: 150px;
		padding-right: 20px;

		@media (min-width: 767px) and (max-width: 1024px) {
			height: 100px;
		}
	}

	[data-reach-slider-orientation='horizontal'] {
		padding: 0 20px 0 10px;
	}
`

const matchesPoolSettings = (product, state) => {
	const tags = Array.isArray(product.tags) ? product.tags : []
	const maxLength = Number(
		tags.find(tag => /8m|10m|12m|15m/.test(tag))?.split('m')[0]
	)
	const minWidth = Number(tags.find(tag => /1m|3m/.test(tag))?.split('m')[0])

	return (
		state.length <= maxLength &&
		state.width >= minWidth &&
		tags.includes(state.shape) &&
		tags.includes(state.surface)
	)
}

const getPoolRecommendation = (availableProducts, state) => {
	const recommendations = availableProducts.reduce((results, product, index) => {
		const tags = Array.isArray(product.tags) ? product.tags : []
		const maxLengthTag = tags.find(tag => /^(8|10|12|15)m$/.test(tag))
		const minWidthTag = tags.find(tag => /^(1|3)m$/.test(tag))
		const supportedShape = tags.find(tag => tag === 'normal' || tag === 'complex')
		const supportedSurface = tags.find(
			tag => tag === 'smooth' || tag === 'coarse'
		)

		if (!maxLengthTag || !minWidthTag || !supportedShape || !supportedSurface) {
			return results
		}

		const recommendation = {
			index,
			length: Math.min(state.length, Number(maxLengthTag.replace('m', ''))),
			width: Math.max(state.width, Number(minWidthTag.replace('m', ''))),
			shape: tags.includes(state.shape) ? state.shape : supportedShape,
			surface: tags.includes(state.surface) ? state.surface : supportedSurface,
			product,
		}
		recommendation.sizeChanged =
			recommendation.length !== state.length ||
			recommendation.width !== state.width
		recommendation.shapeChanged = recommendation.shape !== state.shape
		recommendation.surfaceChanged = recommendation.surface !== state.surface
		recommendation.changeCount =
			Number(recommendation.sizeChanged) +
			Number(recommendation.shapeChanged) +
			Number(recommendation.surfaceChanged)
		recommendation.distance =
			Math.abs(recommendation.length - state.length) +
			Math.abs(recommendation.width - state.width)

		results.push(recommendation)
		return results
	}, [])

	recommendations.sort(
		(a, b) =>
			a.changeCount - b.changeCount ||
			a.distance - b.distance ||
			a.index - b.index
	)

	return recommendations[0]
}

export const WhichModel = ({ location }) => {
	const { loading, error, data } = useQuery(PRODUCT_COLLECTION, {
		client,
	})

	const shapeArray = [
		{
			shape: 'round',
			value: 'normal',
			active: true,
		},
		{
			shape: 'rectangular',
			value: 'normal',
			active: false,
		},
		{
			shape: 'complex',
			value: 'complex',
			active: false,
		},
	]
	const [state, set] = useState({
		width: 3,
		length: 8,
		shape: shapeArray[0].value,
		// shape: 'normal',
		surface: 'smooth',
		select: 'Select',
		bigpool: false,
	})
	const [active, setActive] = useState(shapeArray)
	const [table, setTable] = useState([])
	const [products, setProducts] = useState([])
	const apiData = data && data.products && data.products.items
	const hasData = !!apiData
	const hasProducts = !!(products && products.length >= 1)
	const bigAssPool = state.length >= 16 || state.bigpool
	const hasMatchingProducts =
		!!apiData && apiData.some(product => matchesPoolSettings(product, state))
	const noMatchingProducts = !!apiData && !bigAssPool && !hasMatchingProducts
	const suggestedSettings =
		!loading && noMatchingProducts
			? getPoolRecommendation(apiData, state)
			: undefined

	const InputChange = val => {
		if (val === 0) {
			return 1
		} else if (val > 16) {
			return 16
		} else {
			return val
		}
	}

	const ReplaceActive = index => {
		const x = active.map((x, i) => {
			if (i === index) {
				return { ...x, active: true }
			} else {
				return { ...x, active: false }
			}
		})
		setActive(x)
	}

	useEffect(() => {
		if (!bigAssPool) {
			if (apiData != undefined) {
				if (apiData.length > 0) {
					const products = apiData.filter(product =>
						matchesPoolSettings(product, state)
					)
					return setProducts(products)
				} else {
					setProducts([])
				}
			}
		} else {
			setProducts(apiData)
		}
	}, [state, apiData, hasData, hasProducts, bigAssPool])

	const { addToTable } = useProduct()
	const { height, width } = useWindowResize()

	// const checkLength = (key, brandPage, tagCheck, title) => {
	// 	const items = windowGlobal.sessionStorage.getItem(key)
	// 	const parsedItems = JSON.parse(items)
	// 	if (width > 1023) {
	// 		const checkTable = table.indexOf(title)
	// 		if ( checkTable < 0 ) {
	// 			if ( table.length != 3 ) {
	// 				setTable([...table, title])
	// 			} else {
	// 				alert('Items to compare cannot be more than 3.')
	// 			}
	// 		} else {
	// 			const x = [...table]
	// 			x.splice(checkTable, 1)
	// 			setTable([...x])
	// 		}
	// 	} else {
	// 		const checkTable = table.indexOf(title)
	// 		if ( checkTable < 0 ) {
	// 			if ( table.length != 2 ) {
	// 				setTable([...table, title])
	// 			} else {
	// 				alert('Items to compare cannot be more than 2.')
	// 			}
	// 		} else {
	// 			const x = [...table]
	// 			x.splice(checkTable, 1)
	// 			setTable([...x])
	// 		}
	// 	}
	// }

	// console.log(table, 'table')
	// console.log(products, 'products')

	useEffect(() => {
		const len = width > 1023 ? 3 : 2
		const x =
			products != undefined
				? products
						.filter((x, i) => {
							if (state.length <= 12 && i === 1) return ''
							if (state.length < 7 && state.length > 4 && i === 2) return ''
							if (state.length <= 6 && i === 4) return ''
							return x
						})
						.splice(0, len)
						.map(x => x.seoTitle)
				: []
		setTable(x)
	}, [products, state.length, width])

	const removeCheck = title => {
		const x = table.indexOf(title)

		if (x < 0) {
			if (width > 1023) {
				setTable([...table, title])
			} else if (width <= 1023) {
				if (table.length < 2) {
					setTable([...table, title])
				} else {
					alert('Items to compare cannot be more than 2.')
				}
			}
		} else {
			const z = table.indexOf(title)
			const a = table.filter((x, i) => {
				return z != i ? x : ''
			})
			setTable(a)
		}
	}

	return (
		<Layout>
			<Meta
				title="Which Model"
				keywords={['which model', 'which', 'model', 'pool cleaner']}
				description="Define your pools unique properties to determine which Dolphin pool cleaner is right for you."
				locale="en-ZA"
			/>
			<div css={tw`bg-gray-100 w-full flex flex-col items-center`}>
				<Banner
					bg="https://images.ctfassets.net/ftcnv5up9dxv/6Tq05OfWIP02p5hqRsc1G9/24e45ef0354f61bcb92f1676301e7edb/m-600-sitting-on-pool-edge.jpg?fit=crop&f=bottom&w=1920&h=900&fm=jpg&fl=progressive&q=50"
					role="img"
					aria-label="Maytronics Dolphin M 600 robotic pool cleaner sitting on the pool's edge"
				/>
				<div tw="px-1 w-95 xl:w-11/12 mb-12">
					<Heading>Which model is right for you?</Heading>
					<SubHead>
						Define your pools unique properties to determine which Dolphin pool
						cleaner is right for you.
					</SubHead>
					{/* <pre>{JSON.stringify(state, null, 2)}</pre> */}
					<ContentsHolder tw="mt-6 pt-6 border-t border-gray-200">
						<LeftContent tw="mb-4 lg:mb-0">
							<Line tw="mt-0" />
							<PoolSize>
								<Label htmlFor="pool-surface">Pool Size</Label>
								<div tw="flex flex-row flex-nowrap ml-6 items-center">
									<img
										src="//images.ctfassets.net/ftcnv5up9dxv/16kmPeMrUETjtI5mXKM56R/2841f1f0ea1d2c2128667cdf63beb49d/Asset_Wizard_PoolSize.png?h=150&fm=jpg&fl=progressive&q=100"
										css={tw`w-4/5`}
										alt="Pool Size"
										aria-label="Pool Size"
									/>

									<div tw="flex flex-col items-center justify-center w-1/5">
										<p css={tw`font-bold`}>{state.width}</p>
										<SliderInput
											min={1}
											max={16}
											step={1}
											value={state.width}
											orientation="vertical"
											onChange={value => set({ ...state, width: value })}
										>
											<SliderTrack tw="bg-gray-400 cursor-pointer">
												<SliderTrackHighlight tw="bg-gray-700 cursor-pointer" />
												<SliderHandle tw="h-3 w-3 border-2 border-gray-700 cursor-pointer" />
											</SliderTrack>
										</SliderInput>
									</div>
								</div>
								<div tw="flex flex-row items-center mt-4 xl:mt-4">
									<p css={tw`font-bold`}>{state.length}</p>
									<SliderInput
										min={1}
										max={16}
										step={1}
										value={state.length}
										orientation="horizontal"
										onChange={value => set({ ...state, length: value })}
										tw="w-11/12"
									>
										<SliderTrack tw="bg-gray-400 cursor-pointer">
											<SliderTrackHighlight tw="bg-gray-700 cursor-pointer" />
											<SliderHandle tw="h-3 w-3 border-2 border-gray-700 cursor-pointer" />
										</SliderTrack>
									</SliderInput>
								</div>

								<Inputs>
									<p css={tw`w-2/5`}>
										Length{' '}
										<input
											type="number"
											min="1"
											max="16"
											value={state.length}
											onChange={e => {
												set({
													...state,
													length: InputChange(Number(e.target.value)),
												})
											}}
										/>{' '}
										m
									</p>
									<p css={tw`mx-2 w-1/5`}>
										<span>x</span>
									</p>
									<p css={tw`w-2/5`}>
										Width{' '}
										<input
											type="number"
											min="1"
											max="16"
											value={state.width}
											onChange={e => {
												set({
													...state,
													width: InputChange(Number(e.target.value)),
												})
											}}
										/>{' '}
										m
									</p>
								</Inputs>

								<div css={tw`flex flex-row items-center mt-8 w-11/12 mx-auto`}>
									<input
										type="checkbox"
										id="over16m"
										checked={state.bigpool}
										onChange={() => {
											set({ ...state, bigpool: !state.bigpool })
										}}
									/>
									<label css={tw`ml-2 font-display text-sm`} htmlFor="over16m">
										Over 16m
									</label>
								</div>
							</PoolSize>
							<Line />
							<div tw="flex flex-col justify-between w-full px-4 lg:px-2 pb-8">
								<Label htmlFor="pool-shape">Pool Shape</Label>

								<div tw="flex flex-row pt-4">
									<div tw="flex flex-col w-4/12 items-center justify-center">
										<p tw="w-6/12 mb-3 cursor-pointer">
											<Round
												onClick={() => {
													ReplaceActive(0)
													set({ ...state, shape: 'normal' })
												}}
												color={active[0].active ? '#57c7dd' : '#c3c3c3'}
											/>
										</p>
										<p
											onClick={() => {
												ReplaceActive(0)
												set({ ...state, shape: 'normal' })
											}}
											tw="cursor-pointer capitalize font-display text-sm font-bold"
											css={{
												color: active[0].active ? '#57c7dd' : 'initial',
											}}
										>
											{shapeArray[0].shape}
										</p>
									</div>
									<div tw="flex flex-col w-4/12 items-center justify-center">
										<p tw="w-6/12 mb-3 cursor-pointer">
											<Rectangular
												onClick={() => {
													ReplaceActive(1)
													set({ ...state, shape: 'normal' })
												}}
												color={active[1].active ? '#57c7dd' : '#c3c3c3'}
											/>
										</p>
										<p
											onClick={() => {
												ReplaceActive(1)
												set({ ...state, shape: 'normal' })
											}}
											tw="cursor-pointer capitalize font-display text-sm font-bold"
											css={{
												color: active[1].active ? '#57c7dd' : 'initial',
											}}
										>
											{shapeArray[1].shape}
										</p>
									</div>
									<div tw="flex flex-col w-4/12 items-center justify-center">
										<p tw="w-6/12 mb-3 cursor-pointer">
											<Complex
												onClick={() => {
													ReplaceActive(2)
													set({ ...state, shape: 'complex' })
												}}
												color={active[2].active ? '#57c7dd' : '#c3c3c3'}
											/>
										</p>
										<p
											onClick={() => {
												ReplaceActive(2)
												set({ ...state, shape: 'complex' })
											}}
											tw="cursor-pointer capitalize font-display text-sm font-bold"
											css={{
												color: active[2].active ? '#57c7dd' : 'initial',
											}}
										>
											{shapeArray[2].shape}
										</p>
									</div>
								</div>
							</div>
							<Line />
							<div tw="flex flex-col justify-between w-full rounded px-1 md:px-2 pb-8">
								<Label htmlFor="pool-surface" tw="mb-7">
									Pool Surface
								</Label>
								<Menu>
									<SelectButton id="pool-surface" tw="w-full text-left">
										<span>{state.select}</span>
										<SelectIcon aria-hidden>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
											>
												<path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
											</svg>
										</SelectIcon>
									</SelectButton>
									<SelectList className="form-list">
										<SelectItem
											onSelect={() =>
												set({ ...state, surface: 'smooth', select: 'Tile' })
											}
										>
											Tile
										</SelectItem>
										<SelectItem
											onSelect={() =>
												set({ ...state, surface: 'coarse', select: 'Pebble' })
											}
										>
											Pebble
										</SelectItem>
										<SelectItem
											onSelect={() =>
												set({ ...state, surface: 'coarse', select: 'Concrete' })
											}
										>
											Concrete
										</SelectItem>
										<SelectItem
											onSelect={() =>
												set({ ...state, surface: 'smooth', select: 'Vinyl' })
											}
										>
											Vinyl
										</SelectItem>
									</SelectList>
								</Menu>
							</div>
							<Line />
						</LeftContent>

						<RightContent>
							{!loading && hasProducts && !bigAssPool && (
								<>
									<div tw="flex flex-col flex-wrap sm:flex-row w-full mb-4 md:mb-12 text-center">
										{products
											.filter((x, i) => {
												if (state.length <= 12 && i === 1) return ''
												if (state.length < 7 && state.length > 4 && i === 2)
													return ''
												if (state.length <= 6 && i === 4) return ''
												return x
											})
											.splice(0, 3)
											.map((product, index) => {
												return (
													<div tw="flex flex-col w-full sm:w-6/12 md:w-1/3 mb-8 md:mb-0">
														<div
															key={product.slug}
															role="button"
															tabIndex="-1"
															onClick={() =>
																navigate(`/en-ZA/product/${product.slug}/`)
															}
															onKeyDown={() =>
																navigate(`/en-ZA/product/${product.slug}/`)
															}
															tw="flex flex-col mx-3 bg-white rounded py-4 shadow-lg hover:shadow-outline-gray cursor-pointer h-full"
														>
															<h3 tw="text-2xl font-bold font-display text-gray-700 mb-1 mx-4">
																{index === 0 && 'BEST'}
																{index === 1 && 'BETTER'}
																{index === 2 && 'GOOD'}
															</h3>
															<img
																alt={`Maytronics ${product.seoTitle} robotic pool cleaner`}
																width="300"
																height="300"
																tw="flex w-full bg-gray-200 my-1 px-4"
																src={product.imagesCollection.items[0].url}
															/>
															<h4 tw="text-lg font-normal font-display text-gray-600 my-1 mx-4">
																{product.seoTitle}
															</h4>
															<p tw="text-sm font-normal font-sans text-gray-900 my-2 mx-4">
																{product.seoDescription}
															</p>
															<span tw="font-sans font-bold my-1 mt-auto">
																{product.price.toLocaleString('en-ZA', {
																	style: 'currency',
																	currency: 'ZAR',
																})}
															</span>
														</div>
														<button
															onClick={() =>
																navigate(`/en-ZA/product/${product.slug}/`)
															}
															onKeyDown={() =>
																navigate(`/en-ZA/product/${product.slug}/`)
															}
															tw="cursor-pointer mx-3 my-4 py-3 px-4 xl:px-0 font-display uppercase font-bold rounded border-2 border-gray-400 w-6/12 md:w-8/12 xxl:w-6/12 mx-auto hover:bg-white text-sm lg:text-base"
														>
															View Product
														</button>

														<div tw="flex flex-row items-center justify-center">
															<input
																tw="mr-4 cursor-pointer"
																type="checkbox"
																id={`compare+${product.seoTitle}`}
																onChange={e => {
																	removeCheck(product.seoTitle)
																}}
																checked={
																	table.indexOf(product.seoTitle) < 0
																		? false
																		: true
																}
															/>
															<label
																tw="font-display cursor-pointer"
																htmlFor={`compare+${product.seoTitle}`}
															>
																Add to compare
															</label>
														</div>
													</div>
												)
											})}
									</div>
									<a
										tw="font-display uppercase text-center bg-yellow-500 hover:bg-yellow-400 py-3 px-24 font-bold rounded self-center"
										href="/en-ZA/product-comparison#residential"
										onClick={() => {
											windowGlobal.sessionStorage.setItem(
												'residential',
												JSON.stringify(table)
											)
										}}
									>
										compare
									</a>
								</>
							)}
							{!loading && noMatchingProducts && (
								<div tw="flex w-full mb-6">
									<div tw="flex flex-col w-full md:w-2/3 bg-white rounded p-6 shadow-lg">
										<h3 tw="text-xl font-bold font-display text-gray-700 mb-2">
											No products match your pool settings
										</h3>
										{suggestedSettings ? (
											<>
												<p tw="text-sm font-sans text-gray-900 mb-4">
													Try these settings to see{' '}
													{suggestedSettings.product.seoTitle}:{' '}
													{suggestedSettings.length}m x{' '}
													{suggestedSettings.width}m,{' '}
													{suggestedSettings.shape === 'complex'
														? 'complex'
														: 'round or rectangular'}{' '}
													pool, and{' '}
													{suggestedSettings.surface === 'smooth'
														? 'smooth (tile or vinyl)'
														: 'coarse (pebble or concrete)'}{' '}
													surface.
												</p>
												<button
													type="button"
													onClick={() => {
														if (suggestedSettings.shape !== state.shape) {
															ReplaceActive(
																suggestedSettings.shape === 'complex' ? 2 : 0
															)
														}
														set({
															...state,
															length: suggestedSettings.length,
															width: suggestedSettings.width,
															shape: suggestedSettings.shape,
															surface: suggestedSettings.surface,
															select:
																suggestedSettings.surface === 'smooth'
																	? 'Tile'
																	: 'Pebble',
														})
													}}
													tw="self-start cursor-pointer py-3 px-6 font-display uppercase font-bold rounded border-2 border-gray-400 hover:bg-gray-100 text-sm"
												>
													Apply suggested settings
												</button>
											</>
										) : (
											<p tw="text-sm font-sans text-gray-900">
												Try adjusting your pool size, surface, or shape.
											</p>
										)}
									</div>
								</div>
							)}
							{bigAssPool && (
								<div tw="flex w-full -mx-3 mb-6 text-center">
									<div
										role="button"
										tabIndex="-1"
										tw="flex flex-col w-full md:w-1/3 mx-3 bg-white rounded py-4 shadow-lg hover:shadow-outline-gray cursor-pointer"
									>
										<h3 tw="text-2xl font-bold font-display text-gray-700 mb-1 mx-4">
											SPECIALISED
										</h3>
										<img
											alt={`Maytronics Wave robotic pool cleaner`}
											width="300"
											height="300"
											tw="flex w-full bg-gray-200 my-1 px-4"
											src="https://images.ctfassets.net/ftcnv5up9dxv/1Uy4W3FbHr7znenAWErfyS/5bd064c495f6f2bfc0273b37725cd00b/Wave-100-WB.png?w=500&h=500&q=60&fit=pad&bg=rgb%3Ae5e7eb&fm=jpg&fl=progressive"
										/>
										<h4 tw="text-lg font-normal font-display text-gray-600 my-1 mx-4">
											Commercial Cleaner
										</h4>
										<p tw="text-sm font-normal font-sans text-gray-900 my-2 mx-4">
											Wow, thats a big pool! You might need a commercial
											cleaner, give us a call to discuss your options
										</p>
										<span tw="font-sans font-bold my-1 mt-auto">
                                            0860 DOLPHIN
										</span>
									</div>
								</div>
							)}
						</RightContent>
					</ContentsHolder>

					{loading && <div tw="flex -mx-3 mb-6 text-center">...loading</div>}
				</div>
			</div>
		</Layout>
	)
}

const PRODUCT_COLLECTION = gql`
	query ProductsZa {
		products: productCollection(
			locale: "en-ZA"
			where: {
				seoTitle_in: [
					"Dolphin M 600"
					"Dolphin S 50"
					"Dolphin M 500 WB"
					"Dolphin M 500 CB"
					"Dolphin S 300"
					"Dolphin M3"
				]
				tags_contains_some: [
					"1m"
					"3m"
					"8m"
					"10m"
					"12m"
					"15m"
					"coarse"
					"smooth"
					"normal"
					"complex"
				]
			}
			order: price_DESC
		) {
			total
			items {
				slug
				seoTitle
				seoKeywords
				seoDescription
				price
				sku
				tags
				imagesCollection(limit: 1) {
					items {
						url(
							transform: {
								quality: 60
								format: JPG_PROGRESSIVE
								width: 500
								height: 500
								resizeStrategy: PAD
								backgroundColor: "rgb:e5e7eb"
							}
						)
					}
				}
				brand
				model
				series
				categories {
					title: seoTitle
				}
				shippingClass
			}
		}
	}
`

WhichModel.propTypes = {
	location: PropTypes.object,
	data: PropTypes.object,
}

export default WhichModel
